import { useCallback, useMemo, useRef, useState } from "react";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import type { ReviewRating } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";

/** One undoable review: what to roll back, the card to return to, and the rating to redo. */
export type ReviewUndoEntry = {
  drillType: DrillType;
  fsrsCardId: string;
  grapheme: string;
  rating: ReviewRating;
  /** The `user_card_review` row to soft-delete, which carries the pre-review FSRS log. */
  userCardReviewId: string;
};

/**
 * The current Learn session's stacks of undoable and redoable reviews.
 *
 * Held in `LearnLayoutContext` — above the learn navigator — because review navigation
 * `replace`s rather than pushes (see `useNavigateToLearnCardDestination`), so there is no route
 * history to walk back through and nothing card-scoped survives the hop to the next card.
 *
 * SESSION-SCOPED BY CONSTRUCTION. The stacks live in component state, so leaving Learn unmounts
 * the layout and the history goes with it. That is the intended boundary rather than a
 * limitation: undo rewrites scheduling state, and silently rewinding a review from hours ago
 * (whose card may since have been reviewed again) is not what a back button should mean. It also
 * keeps undo honest without a "was this review already superseded" check on every entry.
 *
 * Undo moves the entry onto the REDO stack; redo re-rates the same card with the same rating
 * through the ordinary commit path, which pushes it back onto the undo stack. Any other new
 * review clears the redo stack, as in every editor. The stacks are mirrored in refs because
 * popping has to RETURN the entry it removed; a `useState` updater is not guaranteed to run
 * synchronously (and runs twice under StrictMode), so the refs are authoritative and the counts
 * exist to re-render `canUndoReview` / `canRedoReview`.
 */
export function useReviewUndoHistory() {
  const undoStackRef = useRef<ReviewUndoEntry[]>([]);
  const redoStackRef = useRef<ReviewUndoEntry[]>([]);
  // Set by `popReviewRedoEntry`: the push that follows is the redo landing, which must not clear
  // the remaining redo entries the way a fresh review does.
  const expectsRedoPushRef = useRef(false);
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  const pushReviewUndoEntry = useCallback((entry: ReviewUndoEntry) => {
    undoStackRef.current = [...undoStackRef.current, entry];
    setUndoCount(undoStackRef.current.length);
    if (expectsRedoPushRef.current) {
      expectsRedoPushRef.current = false;
      return;
    }
    redoStackRef.current = [];
    setRedoCount(0);
  }, []);

  /** Drops the most recent entry and hands it back (and onto the redo stack) for the rollback. */
  const popReviewUndoEntry = useCallback((): ReviewUndoEntry | null => {
    const popped = undoStackRef.current.at(-1);
    if (popped === undefined) {
      return null;
    }
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    setUndoCount(undoStackRef.current.length);
    expectsRedoPushRef.current = false;
    redoStackRef.current = [...redoStackRef.current, popped];
    setRedoCount(redoStackRef.current.length);
    return popped;
  }, []);

  /**
   * Reads the most recent entry WITHOUT consuming it — for surfaces that only need to know what
   * an undo would roll back. Popping is reserved for the "Undo last review" control itself.
   */
  const peekReviewUndoEntry = useCallback((): ReviewUndoEntry | null => {
    return undoStackRef.current.at(-1) ?? null;
  }, []);

  /** The review a redo would re-apply, without consuming it. */
  const peekReviewRedoEntry = useCallback((): ReviewUndoEntry | null => {
    return redoStackRef.current.at(-1) ?? null;
  }, []);

  /** Consumes the redo entry; the caller re-rates that card, and that rating's push keeps the rest. */
  const popReviewRedoEntry = useCallback((): ReviewUndoEntry | null => {
    const popped = redoStackRef.current.at(-1);
    if (popped === undefined) {
      return null;
    }
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    setRedoCount(redoStackRef.current.length);
    expectsRedoPushRef.current = true;
    return popped;
  }, []);

  return useMemo(
    () => ({
      canRedoReview: redoCount > 0,
      canUndoReview: undoCount > 0,
      peekReviewRedoEntry,
      peekReviewUndoEntry,
      popReviewRedoEntry,
      popReviewUndoEntry,
      pushReviewUndoEntry,
    }),
    [
      peekReviewRedoEntry,
      peekReviewUndoEntry,
      popReviewRedoEntry,
      popReviewUndoEntry,
      pushReviewUndoEntry,
      redoCount,
      undoCount,
    ],
  );
}
